// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BaseInput, BaseModal, toast} from "@/components";
import useIsMobile from "@/hooks/useIsMobile";
import useResendEmailVerification from "@/hooks/useResendEmailVerification";
import {postBinanceWithdrawalService} from "@/services/wallet/postBinanceWithdrawal.service";

const MIN_AMOUNT = 10;
const MAX_AMOUNT = 1500;
const FEE = 1;
const NETWORK = "TRX";

const BinanceWithdrawal = ({onBack, onCancel}) => {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [addressMsg, setAddressMsg] = useState({text: "", color: ""});
  const [amountMsg, setAmountMsg] = useState({text: "", color: ""});
  const [addressTouched, setAddressTouched] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const {t} = useTranslation();
  const isMobile = useIsMobile();
  const {resendVerification, isSuccess: isEmailSent, isError: isEmailError} = useResendEmailVerification();

  // Handle email verification success/error feedback
  useEffect(() => {
    if (isEmailSent) {
      toast.success(`${t("emailVerificationSentTitle")} — ${t("emailVerificationSentMessage")}`);
    }
    if (isEmailError) {
      toast.error(`${t("emailVerificationErrorTitle")} — ${t("emailVerificationErrorMessage")}`);
    }
  }, [isEmailSent, isEmailError, t]);

  const resetState = () => {
    setAmount("");
    setAddress("");
    setShowConfirm(false);
    setAmountMsg({text: "", color: ""});
    setAddressMsg({text: "", color: ""});
    setAmountTouched(false);
    setAddressTouched(false);
  };

  const isValidTronAddress = addr => {
    if (!addr || typeof addr !== "string") return false;
    if (!addr.startsWith("T")) return false;
    if (addr.length !== 34) return false;
    return true;
  };

  const validateAmount = val => {
    if (!val) return false;
    const num = Number(val);
    if (!Number.isFinite(num)) return false;
    return num >= MIN_AMOUNT && num <= MAX_AMOUNT;
  };

  const isContinueEnabled = isValidTronAddress(address) && validateAmount(amount);

  const handleContinue = () => {
    setAddressTouched(true);
    setAmountTouched(true);
    // Only show modal if no errors
    if (isValidTronAddress(address) && validateAmount(amount)) {
      setShowConfirm(true);
    }
  };

  // Live validation for amount
  const handleAmountChange = e => {
    const val = e.target.value;
    setAmount(val);
    setAmountTouched(true);
    // Only allow valid number format
    const numberRegex = /^\d*\.?\d*$/;
    if (!val) {
      setAmountMsg({text: t("binanceWithdrawalAmountRequired"), color: "#ff4d4f"});
    } else if (!numberRegex.test(val)) {
      setAmountMsg({text: t("binanceWithdrawalInputErrorNumber"), color: "#ff4d4f"});
    } else {
      const num = Number(val);
      if (!Number.isFinite(num)) {
        setAmountMsg({text: t("binanceWithdrawalInputErrorInvalid"), color: "#ff4d4f"});
      } else if (num < MIN_AMOUNT) {
        setAmountMsg({text: `${t("binanceWithdrawalInputErrorMin")} ${MIN_AMOUNT} USDT.`, color: "#ff4d4f"});
      } else if (num > MAX_AMOUNT) {
        setAmountMsg({text: `${t("binanceWithdrawalInputErrorMax")} ${MAX_AMOUNT} USDT.`, color: "#ff4d4f"});
      } else {
        setAmountMsg({text: t("binanceWithdrwalAmountCorrect"), color: "#52c41a"});
      }
    }
  };

  // Live validation for address
  const handleAddressChange = e => {
    const val = e.target.value;
    setAddress(val);
    setAddressTouched(true);
    if (!val) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorRequired"), color: "#ff4d4f"});
    } else if (!val.startsWith("T")) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorStart"), color: "#ff4d4f"});
    } else if (val.length !== 34) {
      setAddressMsg({text: t("binanceWithdrawalAddressErrorLength"), color: "#ff4d4f"});
    } else {
      setAddressMsg({text: t("binanceWithdrawalAddressCorrect"), color: "#52c41a"});
    }
  };

  const handleConfirm = async () => {
    if (!isValidTronAddress(address)) {
      toast.error("La dirección de wallet (TRC20) es requerida y debe ser válida.");
      setIsPending(false);
      return;
    }
    setIsPending(true);
    try {
      await postBinanceWithdrawalService({
        amount,
        to_address: address,
        network: NETWORK,
      });
      toast.success(`Se ha retirado ${amount} USDT a la dirección ${address} en la red ${NETWORK}.`);
      resetState();
      if (typeof onCancel === "function") onCancel();
    } catch (error) {
      // Get error message from axios error structure or regular error
      const errorMessage = error?.response?.data?.message || error?.message || "";

      // Check if error is due to email not verified
      if (errorMessage.includes("emailNotVerified")) {
        toast.error(t("withdrawalStepsErrorDesc_3"));

        // Automatically trigger email verification resend
        resendVerification();
      } else {
        toast.error(errorMessage || "No se pudo procesar el retiro.");
      }
      setShowConfirm(false);
    }
    setIsPending(false);
  };

  return (
    <div style={{padding: isMobile ? "0 1rem" : "0 2rem", textAlign: "center", fontFamily: "var(--font-family)"}}>
      <h2 style={{fontSize: "clamp(16px, 2.5vw, 18px)"}}>{t("binanceWithdrawalTitle")}</h2>
      <div style={{display: isMobile ? "block" : "flex", justifyContent: "center", gap: "var(--space-7)"}}>
        <div style={{flex: 1}}>
          <BaseInput
            placeholder="Monto a retirar (USDT)"
            value={amount}
            onChange={handleAmountChange}
            onBlur={() => setAmountTouched(true)}
            containerClassName="mb-2"
            type="text"
            className="!border-2 !border-[var(--color-lottery)] focus-visible:!outline-[var(--color-lottery)]"
          />
          {amountTouched && amountMsg.text && (
            <div
              style={{
                color: amountMsg.color,
                fontSize: "var(--font-body)",
                marginBottom: "var(--space-2-5)",
                textAlign: "left",
              }}
            >
              {amountMsg.text}
            </div>
          )}
        </div>
        <div style={{flex: 1}}>
          <BaseInput
            placeholder={t("binanceWithdrawalAddress")}
            value={address}
            onChange={handleAddressChange}
            onBlur={() => setAddressTouched(true)}
            containerClassName="mb-2"
            className="!border-2 !border-[var(--color-lottery)] focus-visible:!outline-[var(--color-lottery)]"
          />
          {addressTouched && addressMsg.text && (
            <div
              style={{
                color: addressMsg.color,
                fontSize: "var(--font-body)",
                marginBottom: "var(--space-2-5)",
                textAlign: "left",
              }}
            >
              {addressMsg.text}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--space-3-5)",
          margin: isMobile ? "24px 0" : "44px 0",
        }}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!isContinueEnabled}
            className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-[var(--color-lottery)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("binanceWithdrawalContinueBtn")}
          </button>
          <button
            type="button"
            onClick={() => {
              resetState();
              if (typeof onBack === "function") onBack();
            }}
            className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
          >
            {t("withdrawalStepsBack")}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            resetState();
            onCancel();
          }}
          className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
        >
          {t("withdrawalStepsCancel")}
        </button>
      </div>
      <BaseModal
        open={showConfirm}
        title={t("binanceWithdrawalConfirmTitle")}
        onClose={() => setShowConfirm(false)}
        size="md"
      >
        <div style={{marginBottom: 26, color: "var(--text-secondary)", fontFamily: "var(--font-family)"}}>
          <div style={{marginBottom: "var(--space-3-5)"}}>
            <strong>{t("binanceWithdrawalAddressModal")}:</strong> <span style={{color: "#52c41a"}}>{address}</span>
          </div>
          <div style={{marginBottom: "var(--space-3-5)"}}>
            <strong>{t("binanceWithdrawalNetwork")}:</strong> <span style={{color: "#ff4d4f"}}>{NETWORK}</span>
          </div>
          <div style={{marginBottom: "var(--space-3-5)"}}>
            <strong>{t("binanceWithdrawalAmount")}:</strong>{" "}
            <span style={{color: "#52c41a"}}>{Number(amount).toFixed(2)} USDT</span>
          </div>
          <div style={{marginBottom: "var(--space-3-5)"}}>
            <strong>{t("binanceWithdrawalCommission")}:</strong>{" "}
            <span style={{color: "#ff4d4f"}}>{FEE.toFixed(2)} USDT</span>
          </div>
        </div>
        <div style={{display: "flex", justifyContent: "space-between", gap: "var(--space-3-5)"}}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isPending}
              className="px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-[var(--color-lottery)] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isPending ? "Procesando..." : t("binanceWithdrawalConfirmBtn")}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowConfirm(false);
                resetState();
              }}
              className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
            >
              {t("withdrawalStepsBack")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowConfirm(false);
              resetState();
            }}
            className="px-6 py-2.5 rounded-full text-sm font-semibold border border-[var(--color-lottery)] text-[var(--color-lottery)] bg-transparent hover:bg-[var(--color-lottery)]/10 transition-colors"
          >
            {t("withdrawalStepsCancel")}
          </button>
        </div>
      </BaseModal>
    </div>
  );
};

BinanceWithdrawal.propTypes = {
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
};

export default BinanceWithdrawal;
